import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import Form from "@/components/Form";
import { QueryTable } from "@/components/query-table/QueryTable";
import SaveButton from "@/components/SaveButton";
import Title from "@/components/Title";
import { Module, PATH_PAYROLL_INSPECTION_DETAIL } from "@/constants";
import useQueryData from "@/hooks/useQueryData";
import { useSyncFormWithQueryData } from "@/hooks/useSyncFormWithQueryData";
import { usePayrollDetails } from "@/modules/payrolls/hooks/usePayrollDetails";
import { usePayrollInspectionColumnOverrides } from "@/modules/payrolls/hooks/usePayrollInspectionColumnOverrides";
import { useUpdatePayrollInspectionsMutation } from "@/modules/payrolls/hooks/useUpdatePayrollInspectionsMutation";
import { isProjectRoleDependent } from "@/modules/projects/lib/isProjectRoleDependent";
import { payrollDepartmentBasedInspection } from "@/schemas/payrollDepartmentBasedInspection";
import { payrollRoleBasedInspection } from "@/schemas/payrollRoleBasedInspection";
import { FetchListRequest } from "@/services/api";
import { PayrollsService } from "@/services/payrolls";
import { useFormBaseline } from "@/hooks/useFormBaseline";
import { useFormDefaultValuesFromQuery } from "@/hooks/useFormDefaultValuesFromQuery";
import { zodResolver } from "@hookform/resolvers/zod";
import { memo, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router-dom";
import { usePayrollBreadcrumbs } from "../hooks/usePayrollBreadcrumbs";
import { usePayrollPermissions } from "../hooks/usePayrollPermissions";
import { PayrollCreateEntryButton } from "./PayrollCreateEntryButton";
import { PayrollCorrectionButton } from "./PayrollCorrectionButton";
import { PayrollInspectionRecordView } from "./PayrollInspectionRecordView";
import { ReadPayroll } from "@/types/payroll";
import { WritePayrollDepartmentBasedInspectionForm } from "../types";
import { convertFormToUpdatePayrollDetails } from "../utils/convertFormToUpdatePayrollDetails";
import { initialColumnVisibility } from "../initialColumnVisibility";
import {
  writePayrollDepartmentBasedInspectionFormSchema,
  writePayrollRoleBasedInspectionFormSchema,
} from "../schema";
import {
  PayrollInspection,
  UpdatePayrollInspection,
  WritePayrollInspectionForm,
} from "../types";

const payrollRoleBasedInspectionDisplaySchema = payrollRoleBasedInspection
  .unwrap()
  .omit({
    contractor_card_mimetype: true,
    contractor_card_filename: true,
  })
  .readonly();

const defaultValues: WritePayrollDepartmentBasedInspectionForm = {
  entries: {},
};

interface PayrollInspectionTableProps {
  payrollData: ReadPayroll;
  payrollId: string;
}

export const PayrollInspectionTable = memo(
  ({ payrollData, payrollId }: PayrollInspectionTableProps) => {
    const { t } = useTranslation();

    const isRoleBased = isProjectRoleDependent(payrollData);

    const permissions = usePayrollPermissions();

    const fetchListFn = useCallback(
      (params: FetchListRequest<PayrollInspection>) =>
        PayrollsService.getInspectionList(
          payrollId,
          payrollData.project_depends_on,
          params,
        ),
      [payrollId, payrollData.project_depends_on],
    );

    const pathBuilder = useCallback(
      (row: PayrollInspection) =>
        generatePath(PATH_PAYROLL_INSPECTION_DETAIL, {
          payrollId,
          entryId: row.id,
        }),
      [payrollId],
    );

    const nameSuffix = isRoleBased
      ? "inspection-role-based"
      : "inspection-department-based";

    const { name, options, query, queryValue } =
      usePayrollDetails<PayrollInspection>({
        fetchListFn,
        nameSuffix,
      });

    const tableName = `${name}-${nameSuffix}`;

    const inspectionTitle = t(
      permissions.canUpdateInspection
        ? "payrolls.views.inspection.title"
        : "payrolls.views.inspection.titleReadOnly",
    );

    const breadcrumbs = usePayrollBreadcrumbs([
      {
        label: inspectionTitle,
      },
    ]);

    const columnOverrides = usePayrollInspectionColumnOverrides(isRoleBased);

    const schema = isRoleBased
      ? payrollRoleBasedInspectionDisplaySchema
      : payrollDepartmentBasedInspection;

    const formSchema = isRoleBased
      ? writePayrollRoleBasedInspectionFormSchema
      : writePayrollDepartmentBasedInspectionFormSchema;

    const mutation = useUpdatePayrollInspectionsMutation(
      payrollData.project_depends_on,
    );

    const form = useForm<WritePayrollInspectionForm>({
      resolver: zodResolver(formSchema),
      defaultValues,
      disabled: !permissions.canUpdateInspection || mutation.isPending,
    });

    const data = useQueryData(query);
    const formDefaultValues = useFormDefaultValuesFromQuery<
      PayrollInspection,
      "entries",
      WritePayrollInspectionForm
    >(data);
    const { baseline, updateBaseline } = useFormBaseline(
      form,
      formDefaultValues,
    );

    const handleSubmit = useCallback(
      async (values: WritePayrollInspectionForm) => {
        const dirtyFields = form.formState.dirtyFields;
        const payload =
          convertFormToUpdatePayrollDetails<UpdatePayrollInspection>(
            values,
            dirtyFields,
          );
        await mutation.mutateAsync(payload);
        updateBaseline();
      },
      [form.formState.dirtyFields, mutation, updateBaseline],
    );

    useSyncFormWithQueryData(data, form);

    return (
      <QueryTable
        schema={schema}
        name={tableName}
        translationKey={name}
        pathBuilder={pathBuilder}
        query={query}
        options={options}
        columnOverrides={columnOverrides}
        tableFeatures={{
          editionHighlight: { baseline },
          initialColumnVisibility,
          exportSettings: {
            moduleName: Module.Payrolls,
            queryParams: {
              payroll_stage: isRoleBased
                ? "inspection-role-based"
                : "inspection-department-based",
              payroll_id: payrollId,
            },
          },
        }}
      >
        <QueryTable.Header
          breadcrumbLabel={`${name}.title.multiple`}
          breadcrumbs={breadcrumbs}
          actions={[permissions.canCorrect && <PayrollCorrectionButton />]}
        />
        <Title>{inspectionTitle}</Title>
        <PayrollInspectionRecordView />
        <QueryTable.Menubar>
          <QueryTable.Menubar.Tools />
          <QueryTable.Menubar.Columns />
          <QueryTable.Menubar.Settings />
        </QueryTable.Menubar>
        <DataTableQueryContextProvider value={queryValue}>
          <Form form={form} handleSubmit={handleSubmit}>
            <QueryTable.Content>
              <QueryTable.Status>
                <QueryTable.DirtyRowsStatus />
              </QueryTable.Status>
            </QueryTable.Content>
            <footer className="flex py-2 justify-between gap-6">
              <PayrollCreateEntryButton />
              {permissions.canUpdateInspection && <SaveButton />}
            </footer>
          </Form>
        </DataTableQueryContextProvider>
      </QueryTable>
    );
  },
);
